import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { icons } from 'seal-ui-components';
import store from '@Store';
import mixpanelEvents from '@Mixpanel';
import { LOG_TYPE, addLog } from '@Service/loggingService';
import { setInStorage, STORAGE_TYPE, getFromStorage } from '@Service/storageService';
import { NOTIFICATION_HANDLER, DOCUMENT_MARKUP, MIX_PANEL } from '@Constants/types';
import { stopAnimation, resetAnimation } from '@Actions/documentActions';
import { sortByRiskSeverityDesc } from '@Utils/jsonUtils';
import { isWordonline, isIE11Browser } from '@Utils/commonUtils';
import useWindowSize from '@Hooks/useWindowSize';
import { Dropdown } from '@Components/UI/dropdown';
import Divider from '@Components/UI/Divider';
import { RadioButton } from '@Components/UI/RadioButton';
import AnalyzeDocument from '@Components/AnalyzeDocument';
import Worker from '@Workers/markupCounter.worker';
import { HitRow, PopupConfirm, ProcessingDialog, ArrowDown } from './components';
import {
    ooxmlStr,
    clearOoxmlStr,
    makeChangeToTrackPromise,
    deleteDSChangeTagTextPromise,
    deleteDSChangeTagPromise,
    ClearAllMarkup,
    highlightAllHists,
    addContentControlBindings
} from '@Service/wordApiService';
import {
    MarkupContainerWrapper,
    RadioTitleContainer,
    InfoIconContainer,
    WarningContainer,
    Instruction,
    InstructionContainer,
    ButtonsContainer,
    IconContainer,
    HitContainer,
    Message,
    ButtonDropWrapper,
    StyledButton,
    DropDownWrapper,
    MarkActionText
} from './MarkupPanel.style';

const worker = new Worker();

const MarkupPanel = () => {
    const [openMarkUpOptions, setOpenMarkUpOptions] = React.useState(false);
    const [openClearMarkOptions, setOpenClearMarkOptions] = React.useState(false);

    const [trackChangesCheckEnabled, setTrackChangesCheckEnabled] = useState(false);

    const {
        metadata,
        trackChangesCheckingEnabled,
        isMarkupFailed,
        selectedContentControl,
        overlapMap,
        overlapMapReady,
        contentControlClicked
    } = useSelector(state => state.document);

    let showControls = metadata && metadata.items && metadata.items.length > 0;

    const [isConfirmPopupHidden, setConfirmPopup] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [markupOperation, setMarkupOperation] = useState(showControls ? 'hits' : '');

    const { scorecard } = useSelector(state => state.scoreCard);

    const { riskFlagsInput } = useSelector(state => state.scoreCardFilters);

    //handle if riskFlagsInput is null
    const riskFlags = riskFlagsInput ? riskFlagsInput : {};

    let { items = [] } = metadata;

    const { scoreCardElementList = [] } = scorecard ? scorecard.data : {};

    const [selectedHits, setSelectedHits] = useState([]);
    const [allHitsData, setAllHitsData] = useState([]);

    const dataset = Object.keys(riskFlags).map(key => riskFlags[key]);

    const [hitsCount, setHitsCount] = useState(0);
    const [riskMapCount, setRiskMapCount] = useState({});

    const hitsSelected = () => {
        setMarkupOperation('hits');
        if (showControls) {
            setIsOpen(true);
        }
    };

    const missesSelected = () => {
        setMarkupOperation('misses');
        if (showControls) {
            setIsOpen(false);
        }
    };

    const getCountData = () => {
        worker.postMessage({ scoreCardElementList, items });

        worker.onmessage = e => {
            const { count, riskCountMap, allExtractionsData } = e.data;

            setHitsCount(count);
            setRiskMapCount(riskCountMap);
            setSelectedHits([...selectedHits, ...Object.keys(riskCountMap)]);
            setAllHitsData(allExtractionsData);
            hitsSelected();
        };

        worker.onerror = e => {
            addLog(LOG_TYPE.ERROR, 'MarkupPanel.MarkupCounterWorker', e);
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: 'Something went wrong with Counting Hits '
            });
        };
    };

    useLayoutEffect(
        () => {
            setTimeout(() => getCountData(), 200);
        },
        [scoreCardElementList.length, items.length]
    );

    const size = useWindowSize();

    /* eslint-disable no-empty-function */

    useEffect(
        () => {
            if (trackChangesCheckEnabled !== trackChangesCheckingEnabled) {
                setTrackChangesCheckEnabled(trackChangesCheckingEnabled);
            }
            return () => {};
        },
        [trackChangesCheckingEnabled]
    );

    const applyBindingsFollowingReload = () => {
        const markupOp = getFromStorage('MarkupOperation', STORAGE_TYPE.PROPERTY_BAG);
        addLog(LOG_TYPE.INFO, 'MarkupPanel.reload', `markupOp=${markupOp}`);
        if (markupOp === 'hits') {
            const rootReducer = store.getState();
            const { document, scoreCard } = rootReducer;
            const { clickEventHandlersApplied, documentStatus, markUpInProgress } = document;
            const { scorecardStatus } = scoreCard;

            if (
                markUpInProgress === 'NotStarted' ||
                markUpInProgress === 'Comparing' ||
                markUpInProgress === 'Compared'
            ) {
                setTimeout(() => applyBindingsFollowingReload(), 50);
                return;
            }

            if (markUpInProgress === 'ClickEventHandlersComplete') return;

            addLog(
                LOG_TYPE.INFO,
                'MarkupPanel.reload',
                `clickEventHandlersApplied=${clickEventHandlersApplied}`
            );
            addLog(LOG_TYPE.INFO, 'MarkupPanel.reload', `documentStatus=${documentStatus}`);
            addLog(LOG_TYPE.INFO, 'MarkupPanel.reload', `scorecardStatus=${scorecardStatus}`);

            if (
                !clickEventHandlersApplied &&
                documentStatus === 'Processed' &&
                scorecardStatus === 'Processed'
            ) {
                store.dispatch({
                    type: DOCUMENT_MARKUP.PROCESSING_DIALOG_VISIBLE,
                    payload: true
                });

                addContentControlBindings();
            }
        }
    };

    let retryCounter = 0;
    const reload = () => {
        addLog(LOG_TYPE.INFO, 'MarkupPanel.reload', 'Start');
        const isMarkupApplied = getFromStorage('MarkupApplied', STORAGE_TYPE.PROPERTY_BAG);
        if (isMarkupApplied && isMarkupApplied === 'Yes') {
            // when switch to the markup tab, check that we have an overlap map is populate
            // this should handle the scenario where the add-in reloads
            if (overlapMap.length === 0 && !overlapMapReady) {
                const rootReducer = store.getState();
                const { document } = rootReducer;
                const { metadata: theMetadata } = document;
                let { items: theItems } = theMetadata;
                if (theItems === undefined) {
                    theItems = [];
                }

                // if we have scorecard data but no labels data then we're still waiting for the data to download from the backend
                if (
                    scoreCardElementList &&
                    scoreCardElementList.length > 0 &&
                    theItems &&
                    theItems.length === 0
                ) {
                    // metadata has not arrived yet, try again in 1 sec
                    retryCounter++;
                    if (retryCounter < 10) {
                        setTimeout(() => reload(), 1000);
                        return;
                    }
                }

                items = theItems;

                if (items.length > 0 && scoreCardElementList && scoreCardElementList.length > 0) {
                    worker.postMessage({ scoreCardElementList, items });

                    worker.onmessage = e => {
                        const { riskCountMap, allExtractionsData } = e.data;

                        const selectionText = getFromStorage(
                            'MarkupSelection',
                            STORAGE_TYPE.PROPERTY_BAG
                        );
                        let selection = [];
                        if (selectionText && selectionText.length > 0) {
                            selection = JSON.parse(selectionText);
                        }
                        let selectedHitsChoices = [];
                        if (selection && selection !== []) {
                            selectedHitsChoices = selection;
                        } else {
                            selectedHitsChoices = [...selectedHits, ...Object.keys(riskCountMap)];
                        }

                        const markupOp = getFromStorage(
                            'MarkupOperation',
                            STORAGE_TYPE.PROPERTY_BAG
                        );
                        const misses = markupOp === 'misses';
                        highlightAllHists(allExtractionsData, selectedHitsChoices, false, misses);
                    };
                }
            }

            setTimeout(() => applyBindingsFollowingReload(), 50);
        }
    };

    useEffect(() => {
        setTimeout(() => reload(), 1000);
        return () => {};
    }, []);

    const getAnswer = rule => {
        let answerValue = '';
        if (
            rule &&
            rule.answerField &&
            rule.answerField.values &&
            rule.answerField.values.length > 0
        ) {
            for (let v = 0; v < rule.answerField.values.length; v++) {
                const theValue = rule.answerField.values[v];
                if (theValue.origin) {
                    const origin = theValue.origin.toString();
                    if ((origin === 'bpm' || origin === 'BPM') && theValue.value) {
                        answerValue = theValue.value;
                    }
                }
            }
        }

        return answerValue;
    };

    const getScorecardScore = rule => {
        let scoreValue = -1;
        if (rule && rule.analytics && rule.analytics.length > 0) {
            for (let a = 0; a < rule.analytics.length; a++) {
                const analytic = rule.analytics[a];
                if (analytic && analytic.values && analytic.values.length > 0) {
                    for (let v = 0; v < analytic.values.length; v++) {
                        const value = analytic.values[v];
                        if (
                            value &&
                            value.origin &&
                            value.origin === 'CORE_MODEL' &&
                            value.startOffset &&
                            value.endOffset &&
                            value.startOffset.toString() === selectedContentControl.startOffset &&
                            value.endOffset.toString() === selectedContentControl.endOffset
                        ) {
                            // setAnalyticHitIndex(v);
                            // setAnalyticHitValue(value.value);

                            if (value.attributes && value.attributes.length) {
                                const scoreAttribute = value.attributes.find(
                                    attr => attr.name === 'score'
                                );

                                if (scoreAttribute && scoreAttribute.value) {
                                    scoreValue = scoreAttribute.value;
                                }
                            }
                        }
                    }
                }
            }
        }

        return scoreValue;
    };

    const getPolicyScore = policy => {
        let scoreValue = -1;
        if (policy.values && policy.values.length > 0) {
            for (let v = 0; v < policy.values.length; v++) {
                const value = policy.values[v];
                if (
                    value &&
                    value.startOffset &&
                    value.endOffset &&
                    value.startOffset.toString() === selectedContentControl.startOffset &&
                    value.endOffset.toString() === selectedContentControl.endOffset
                ) {
                    for (let a = 0; a < value.attributes.length; a++) {
                        const attribute = value.attributes[a];
                        if (
                            attribute.name === 'sca_score' ||
                            attribute.name === 'SCORE_KEY' ||
                            attribute.name === 'score'
                        ) {
                            scoreValue = attribute.value;
                        }
                    }
                }
            }
        }

        return scoreValue;
    };

    const getPolicyData = (policyName, startOffset, endOffset) => {
        let foundPolicy = null;
        if (selectedContentControl && metadata && metadata.items && metadata.items.length > 0) {
            for (let i = 0; i < metadata.items.length; i++) {
                const item = metadata.items[i];
                if (item.name.toString() === policyName.toString()) {
                    if (item && item.values && item.values.length > 0) {
                        for (let j = 0; j < item.values.length; j++) {
                            const v = item.values[j];
                            if (
                                v.startOffset &&
                                v.endOffset &&
                                v.startOffset.toString() === startOffset.toString() &&
                                v.endOffset.toString() === endOffset.toString()
                            ) {
                                foundPolicy = item;
                            }
                        }
                    }
                }
            }
        }

        return foundPolicy;
    };

    const getRuleData = (topic, subtopic, startOffset, endOffset) => {
        let foundRule = null;
        if (
            selectedContentControl &&
            scorecard &&
            scorecard.data &&
            scorecard.data.scoreCardElementList &&
            scorecard.data.scoreCardElementList.length > 0
        ) {
            for (let i = 0; i < scorecard.data.scoreCardElementList.length; i++) {
                const elem = scorecard.data.scoreCardElementList[i];

                if (
                    elem.topic.toString() === topic.toString() &&
                    elem.subtopic.toString() === subtopic.toString()
                ) {
                    if (elem.analytics && elem.analytics.length > 0) {
                        elem.analytics.forEach(analytic => {
                            if (analytic.values && analytic.values.length > 0) {
                                analytic.values.forEach(value => {
                                    if (
                                        value.startOffset &&
                                        value.endOffset &&
                                        value.startOffset.toString() === startOffset.toString() &&
                                        value.endOffset.toString() === endOffset.toString()
                                    ) {
                                        foundRule = elem;
                                    }
                                });
                            }
                        });
                    }
                }
            }
        }

        return foundRule;
    };

    useEffect(
        () => {
            const rootReducer = store.getState();
            const { document } = rootReducer;
            const { markupDetails: existingMarkupDetails, markupDetailsTimeStamp } = document;

            let diff = 9999;
            if (markupDetailsTimeStamp) {
                const nowDate = new Date();
                diff = nowDate.getTime() - markupDetailsTimeStamp.getTime();
            }

            let rule = null;
            let policy = null;
            if (selectedContentControl) {
                rule = getRuleData(
                    selectedContentControl.topic,
                    selectedContentControl.subtopic,
                    selectedContentControl.startOffset,
                    selectedContentControl.endOffset
                );

                policy = getPolicyData(
                    selectedContentControl.policyName,
                    selectedContentControl.startOffset,
                    selectedContentControl.endOffset
                );
            }

            let primaryMarkup = null;
            if (rule) {
                primaryMarkup = {
                    riskSeverity: rule.riskSeverity,
                    riskLevel: rule.riskLevel,
                    riskColor: rule.riskColor,
                    answerField: getAnswer(rule),
                    question: rule.question,
                    topic: rule.topic,
                    subtopic: rule.subtopic,
                    score: getScorecardScore(rule)
                };
            }

            if (policy) {
                primaryMarkup = {
                    policyName: policy.name,
                    score: getPolicyScore(policy),
                    riskSeverity: 999
                };
            }

            const markupDetails = [];
            if (primaryMarkup) {
                markupDetails.push(primaryMarkup);
            }

            if (overlapMapReady && overlapMap && overlapMap.length > 0 && selectedContentControl) {
                const overlap = overlapMap.find(
                    f => f.bindingname === selectedContentControl.bindingName
                );
                if (overlap) {
                    for (let i = 0; i < overlap.overlapList.length; i++) {
                        const overlapBindingName = overlap.overlapList[i];

                        // now fetch the details for this binding name
                        const nextOverlap = overlapMap.find(
                            x => x.bindingname === overlapBindingName
                        );

                        if (nextOverlap) {
                            let nextMarkup = null;
                            if (nextOverlap.bindingname.substring(0, 1) === 'S') {
                                nextMarkup = {
                                    riskSeverity: nextOverlap.riskSeverity,
                                    riskLevel: nextOverlap.riskLevel,
                                    riskColor: nextOverlap.riskColor,
                                    answerField: getAnswer(nextOverlap),
                                    question: nextOverlap.question,
                                    topic: nextOverlap.topic,
                                    subtopic: nextOverlap.subtopic,
                                    score: getScorecardScore(nextOverlap)
                                };
                            } else {
                                nextMarkup = {
                                    policyName: nextOverlap.name,
                                    score: getPolicyScore(nextOverlap)
                                };
                            }

                            markupDetails.push(nextMarkup);
                        }
                    }
                }
            }

            if (markupDetails && markupDetails.length > 0) {
                if (contentControlClicked) {
                    // nested CC hack to combine results
                    let delay = 500;
                    if (isIE11Browser()) {
                        delay = 1000;
                    }
                    if (diff < delay) {
                        existingMarkupDetails.forEach(existingEntry => {
                            let found = false;
                            markupDetails.forEach(entry => {
                                if (entry.policyName) {
                                    if (entry.policyName === existingEntry.policyName) {
                                        found = true;
                                    }
                                } else {
                                    if (
                                        entry.topic === existingEntry.topic &&
                                        entry.subtopic === existingEntry.subtopic &&
                                        entry.startOffset === existingEntry.startOffset &&
                                        entry.endOffset === existingEntry.endOffset &&
                                        entry.policyName === existingEntry.policyName
                                    ) {
                                        found = true;
                                    }
                                }
                            });

                            if (!found) {
                                markupDetails.push(existingEntry);
                            }
                        });
                    }
                }

                store.dispatch({
                    type: DOCUMENT_MARKUP.MARKUP_DETAILS,
                    payload: markupDetails.sort(sortByRiskSeverityDesc)
                });

                if (contentControlClicked) {
                    store.dispatch({ type: DOCUMENT_MARKUP.TOGGLE_MARKUP_DETAILS, payload: true });
                    store.dispatch({ type: DOCUMENT_MARKUP.RESET_CLICK_FLAG });
                }
            }

            return () => {};
        },
        [selectedContentControl]
    );

    const closeDialog = () => {
        addLog(LOG_TYPE.INFO, 'MarkupPanel.closeDialog', 'Start');
        store.dispatch({ type: DOCUMENT_MARKUP.PROCESSING_DIALOG_VISIBLE, payload: false });
        store.dispatch({
            type: DOCUMENT_MARKUP.MARKUP_FAILURE,
            payload: false
        });
        store.dispatch({ type: DOCUMENT_MARKUP.CLEAR_BACKGROUND_PROCESS_DATA });
        store.dispatch({
            type: DOCUMENT_MARKUP.MARKUP_RUNNING,
            payload: 'NotStarted'
        });
    };

    useEffect(
        () => {
            addLog(
                LOG_TYPE.INFO,
                'MarkupPanel.useEffect[isMarkupFailed]',
                `isMarkupFailed=${isMarkupFailed}`
            );
            if (isMarkupFailed) {
                closeDialog();
            }
        },
        [isMarkupFailed]
    );

    const isDialogVisible = () => {
        const rootReducer = store.getState();
        const { document } = rootReducer;
        const { processingDialogVisible } = document;
        return processingDialogVisible;
    };

    const isCancelling = () => {
        const rootReducer = store.getState();
        const { document } = rootReducer;
        const { isMarkupCancelling: cancelling } = document;
        return cancelling;
    };

    const callBackClearAll = () => {
        addLog(LOG_TYPE.INFO, 'MarkupPanel.callBackClearAll', 'Start');
        if (isDialogVisible && !isCancelling()) {
            ClearAllMarkup()
                .then(() => {
                    store.dispatch({ type: DOCUMENT_MARKUP.MARKUP_EVENTS_APPLIED, payload: false });
                    setInStorage('MarkupApplied', 'No', STORAGE_TYPE.PROPERTY_BAG);
                    setInStorage('MarkupSelection', '[]', STORAGE_TYPE.PROPERTY_BAG);
                    closeDialog();
                })
                .catch(err => {
                    addLog(LOG_TYPE.ERROR, 'MarkupPanel.callBackClearAll', err);
                    store.dispatch({
                        type: NOTIFICATION_HANDLER.ERROR,
                        payload: 'Something went wrong when clearing the markup'
                    });
                    closeDialog();
                });
        }
    };

    const callBackHighlightAllHits = showMisses => {
        addLog(LOG_TYPE.INFO, 'MarkupPanel.callBackHighlightAllHits', 'Start');
        if (isDialogVisible && !isCancelling()) {
            ClearAllMarkup()
                .then(() => {
                    store.dispatch({ type: DOCUMENT_MARKUP.MARKUP_EVENTS_APPLIED, payload: false });
                    setInStorage('MarkupApplied', 'Yes', STORAGE_TYPE.PROPERTY_BAG);
                    setInStorage(
                        'MarkupSelection',
                        JSON.stringify(selectedHits),
                        STORAGE_TYPE.PROPERTY_BAG
                    );
                    setInStorage('MarkupOperation', markupOperation, STORAGE_TYPE.PROPERTY_BAG);
                    highlightAllHists(allHitsData, selectedHits, true, showMisses);
                })
                .catch(err => {
                    addLog(LOG_TYPE.ERROR, 'MarkupPanel.callBackHighlightAllHits', err);
                    store.dispatch({
                        type: NOTIFICATION_HANDLER.ERROR,
                        payload: 'Something went wrong when clearing the markup'
                    });
                    closeDialog();
                });
        }
    };

    const resetFlags = () => {
        addLog(LOG_TYPE.INFO, 'MarkupPanel.resetFlags', 'Start');
        store.dispatch({ type: DOCUMENT_MARKUP.MARKUP_CANCEL_FLAG, payload: false });
        store.dispatch({ type: DOCUMENT_MARKUP.MARKUP_RUNNING, payload: 'NotStarted' });
        store.dispatch({ type: DOCUMENT_MARKUP.PROCESSING_DIALOG_VISIBLE, payload: false });
    };

    const endCount = (callback, beforeCount, showMisses) => {
        store.dispatch({
            type: DOCUMENT_MARKUP.MARKUP_RUNNING,
            payload: 'Checking'
        });

        const ooxmlString = ooxmlStr();

        if (ooxmlString !== '') {
            const afterCount = (ooxmlString.match(/w:ins/g) || []).length;
            if (!isWordonline()) {
                deleteDSChangeTagPromise();
            } else {
                deleteDSChangeTagTextPromise();
            }

            if (afterCount > beforeCount) {
                clearOoxmlStr();
                store.dispatch({
                    type: NOTIFICATION_HANDLER.WARNING,
                    payload:
                        'Track Changes is currently enabled. Please disabled Track Changes before mark up'
                });

                store.dispatch({
                    type: DOCUMENT_MARKUP.TRACK_CHANGES,
                    payload: true
                });

                // reset flags
                resetFlags();

                store.dispatch({
                    type: DOCUMENT_MARKUP.CHECK_STALE_STATUS,
                    payload: { staleStatus: '', staleResult: 'Good' }
                });
            } else {
                store.dispatch({
                    type: DOCUMENT_MARKUP.MARKUP_RUNNING,
                    payload: 'Comparing'
                });

                callback(showMisses);
            }
        } else {
            setTimeout(() => endCount(callback, beforeCount, showMisses), 1000);
        }
    };

    const startCount = (callback, showMisses) => {
        const ooxmlString = ooxmlStr();

        store.dispatch({
            type: DOCUMENT_MARKUP.TRACK_CHANGES,
            payload: false
        });

        if (ooxmlString !== '') {
            const beforeCount = (ooxmlString.match(/w:ins/g) || []).length;
            let trackRevisionsEveryone = false;
            if (ooxmlString.indexOf('w:trackRevisions') > -1) {
                trackRevisionsEveryone = true;
            }

            if (trackRevisionsEveryone === true) {
                clearOoxmlStr();
                store.dispatch({
                    type: NOTIFICATION_HANDLER.WARNING,
                    payload:
                        'Track Changes is currently enabled. Please disabled Track Changes before mark up'
                });

                store.dispatch({
                    type: DOCUMENT_MARKUP.TRACK_CHANGES,
                    payload: true
                });

                // reset flags
                resetFlags();

                store.dispatch({
                    type: DOCUMENT_MARKUP.CHECK_STALE_STATUS,
                    payload: { staleStatus: '', staleResult: 'Good' }
                });
            } else {
                makeChangeToTrackPromise().then(() => {
                    setTimeout(() => endCount(callback, beforeCount, showMisses), 1000);
                });
            }
        } else {
            setTimeout(() => startCount(callback, showMisses), 1000);
        }
    };

    const trackChangesChecking = withTrackChanges => {
        store.dispatch({
            type: DOCUMENT_MARKUP.TRACK_CHANGES_CHECKING_ENABLED,
            payload: withTrackChanges
        });

        const mixpanelEventProps = {
            risksToShow: selectedHits.join(', '),
            skipTrackChanges: !withTrackChanges
        };

        store.dispatch({
            type: MIX_PANEL.MIXPANEL_DATA_MARKUP,
            payload: mixpanelEventProps
        });
    };

    const clearAll = withTrackChanges => {
        addLog(LOG_TYPE.INFO, 'MarkupPanel.clearAll', 'Start');

        // reset flags
        resetFlags();

        // show progress dialog
        store.dispatch({ type: DOCUMENT_MARKUP.PROCESSING_DIALOG_VISIBLE, payload: true });

        clearOoxmlStr();
        if (withTrackChanges) {
            startCount(callBackClearAll, false);
            return;
        }

        trackChangesChecking(withTrackChanges);

        if (isDialogVisible && !isCancelling()) {
            ClearAllMarkup()
                .then(() => {
                    store.dispatch({ type: DOCUMENT_MARKUP.MARKUP_EVENTS_APPLIED, payload: false });
                    setInStorage('MarkupApplied', 'No', STORAGE_TYPE.PROPERTY_BAG);
                    setInStorage('MarkupSelection', '[]', STORAGE_TYPE.PROPERTY_BAG);
                    closeDialog();
                })
                .catch(err => {
                    addLog(LOG_TYPE.ERROR, 'MarkupPanel.clearAll', err);
                    store.dispatch({
                        type: NOTIFICATION_HANDLER.ERROR,
                        payload: 'Something went wrong when clearing the markup'
                    });
                    closeDialog();
                });
        }
    };

    const dialogVisible = isDialogVisible();

    const handleSelectScorecards = (e, title) => {
        let allChecked = [...selectedHits];

        if (e.target.checked) {
            if (title == 'label') {
                allChecked = ['label', ...Object.keys(riskFlags)];
            } else {
                allChecked.push(title);
            }
        } else {
            allChecked = allChecked.filter(x => x != title).filter(x => x != 'label');
        }

        setSelectedHits(allChecked);
    };

    const renderHitOptions = () => (
        <>
            {dataset.map(
                ({ text, color }, index) =>
                    riskMapCount[text] ? (
                        <div key={`${color}_${text}_${index}`}>
                            <HitRow
                                withBar
                                isHitChecked={selectedHits.includes(text)}
                                color={color}
                                progress={riskMapCount[text]}
                                title={text}
                                text={text}
                                max={hitsCount}
                                roundBorder
                                onSelect={handleSelectScorecards}
                            />
                            <Divider />
                        </div>
                    ) : null
            )}
            {hitsCount > 0 && (
                <>
                    <HitRow
                        withBar
                        isHitChecked={selectedHits.includes('label')}
                        color={'#5b8bff'}
                        progress={hitsCount}
                        title={'Items not assigned a risk rating'}
                        text={'label'}
                        max={hitsCount}
                        onSelect={handleSelectScorecards}
                    />
                    <Divider />
                </>
            )}
        </>
    );

    const onCancel = () => {
        setConfirmPopup(!isConfirmPopupHidden);
    };

    const expandRiskMenu = () => {
        if (showControls) {
            setIsOpen(!isOpen);
        }
    };

    const applyMarkupOnClick = () => {
        trackChangesChecking(true);
        setConfirmPopup(false);

        const mixpanelEventProps = {
            risksToShow: selectedHits.join(', ')
        };

        store.dispatch({
            type: MIX_PANEL.MIXPANEL_DATA_MARKUP,
            payload: mixpanelEventProps
        });

        mixpanelEvents.applyMarkup();
    };

    const applyMarkup = showMisses => {
        addLog(LOG_TYPE.INFO, 'MarkupPanel.markupAllHits', 'Start');

        resetFlags();

        store.dispatch({
            type: DOCUMENT_MARKUP.MARKUP_RUNNING,
            payload: 'Comparing'
        });

        // show progress dialog
        store.dispatch({ type: DOCUMENT_MARKUP.PROCESSING_DIALOG_VISIBLE, payload: true });

        clearOoxmlStr();
        if (trackChangesCheckEnabled) {
            startCount(callBackHighlightAllHits, showMisses);
            return;
        }

        trackChangesChecking(trackChangesCheckEnabled);

        if (isDialogVisible && !isCancelling()) {
            ClearAllMarkup()
                .then(() => {
                    store.dispatch({ type: DOCUMENT_MARKUP.MARKUP_EVENTS_APPLIED, payload: false });
                    setInStorage('MarkupApplied', 'Yes', STORAGE_TYPE.PROPERTY_BAG);
                    setInStorage(
                        'MarkupSelection',
                        JSON.stringify(selectedHits),
                        STORAGE_TYPE.PROPERTY_BAG
                    );
                    setInStorage('MarkupOperation', markupOperation, STORAGE_TYPE.PROPERTY_BAG);
                    highlightAllHists(allHitsData, selectedHits, true, showMisses);
                })
                .catch(err => {
                    addLog(LOG_TYPE.ERROR, 'MarkupPanel.markupAllHits', err);
                    store.dispatch({
                        type: NOTIFICATION_HANDLER.ERROR,
                        payload: 'Something went wrong when clearing the markup'
                    });
                    closeDialog();
                });
        }
    };

    const markupAllHits = () => {
        applyMarkup(false);
    };

    const markupMisses = () => {
        applyMarkup(true);
    };

    const operations = {
        hits: markupAllHits,
        misses: markupMisses
    };
    const onConfirm = () => {
        setConfirmPopup(!isConfirmPopupHidden);

        operations[markupOperation]();
    };

    const INSTRUCTION_WIDTH = isNaN(0.85 * size.width) ? '80%' : 0.85 * size.width;
    // need to send an actual data object into the <AnalyzeDocument> component otherwise the child component (this component) will not render
    const emptyMetadata = { items: [] };

    const canNotAnalyzeHits = !Boolean(selectedHits.length) && markupOperation == 'hits';
    return (
        <AnalyzeDocument
            statues={['Initializing', 'Analyzing document', '']}
            documentStatus="Processed"
            pageType="labels"
            data={emptyMetadata}
            stopAnimation={stopAnimation}
            resetAnimation={resetAnimation}
        >
            <MarkupContainerWrapper>
                <div>
                    <HitContainer>
                        <RadioTitleContainer>
                            <RadioButton
                                name="preset"
                                value={1}
                                checked={markupOperation === 'hits'}
                                onChange={() => hitsSelected()}
                                disabled={!showControls}
                            >
                                Risk level
                            </RadioButton>
                        </RadioTitleContainer>
                        <IconContainer onClick={() => expandRiskMenu()} disabled={!showControls}>
                            {isOpen ? <icons.ChevronUpSmallIcon /> : <icons.ChevronDownSmallIcon />}
                        </IconContainer>
                    </HitContainer>
                    {isOpen && renderHitOptions()}
                    <RadioTitleContainer>
                        <RadioButton
                            name="preset"
                            value={2}
                            checked={markupOperation === 'misses'}
                            onChange={() => missesSelected()}
                            disabled={!showControls}
                        >
                            Unknown
                        </RadioButton>
                        <div data-tip data-for="markup-missed-info" data-place="bottom">
                            <InfoIconContainer />
                        </div>
                        <ReactTooltip
                            id="markup-missed-info"
                            effect="solid"
                            className="white-tooltip"
                        >
                            Analyzed, but contains text that
                            <br /> is not recognized by the system
                        </ReactTooltip>
                    </RadioTitleContainer>
                </div>
                <WarningContainer>
                    <icons.WarningMediumIcon />
                    <InstructionContainer>
                        <Instruction width={INSTRUCTION_WIDTH}>
                            Applying or clearing markup will overwrite any existing color formatting
                        </Instruction>

                        <Instruction width={INSTRUCTION_WIDTH}>
                            Save your document and disable track changes before applying or clearing
                            markup
                        </Instruction>

                        {markupOperation === 'misses' && (
                            <Instruction width={INSTRUCTION_WIDTH}>
                                Recognized language will be greyed out
                            </Instruction>
                        )}
                    </InstructionContainer>
                </WarningContainer>
                <ButtonsContainer>
                    <ButtonDropWrapper>
                        <StyledButton
                            size="medium"
                            appearance="secondary"
                            onClick={() => clearAll(true)}
                        >
                            Clear Markup
                        </StyledButton>
                        <Dropdown
                            open={openClearMarkOptions}
                            onDismiss={() => setOpenClearMarkOptions(false)}
                        >
                            <Dropdown.Toggle>
                                <div onClick={() => setOpenClearMarkOptions(!openClearMarkOptions)}>
                                    <ArrowDown
                                        color="black"
                                        containerStyle={{
                                            border: '1px solid rgb(117,116,123)',
                                            borderLeft: 0,
                                            backgroundColor: 'white'
                                        }}
                                    />
                                </div>
                            </Dropdown.Toggle>
                            <Dropdown.Content>
                                <DropDownWrapper>
                                    <MarkActionText
                                        onClick={() => {
                                            setOpenClearMarkOptions(false);
                                            clearAll(false);
                                        }}
                                    >
                                        Skip track changes check
                                    </MarkActionText>
                                </DropDownWrapper>
                            </Dropdown.Content>
                        </Dropdown>
                    </ButtonDropWrapper>
                    <ButtonDropWrapper disabled={!showControls || canNotAnalyzeHits}>
                        <StyledButton
                            size="medium"
                            appearance="primary"
                            onClick={applyMarkupOnClick}
                        >
                            Apply Markup
                        </StyledButton>
                        <Dropdown
                            open={openMarkUpOptions}
                            onDismiss={() => setOpenMarkUpOptions(false)}
                        >
                            <Dropdown.Toggle>
                                <div onClick={() => setOpenMarkUpOptions(!openMarkUpOptions)}>
                                    <ArrowDown
                                        color="white"
                                        containerStyle={{ backgroundColor: '#0368FF' }}
                                    />
                                </div>
                            </Dropdown.Toggle>
                            <Dropdown.Content>
                                <DropDownWrapper>
                                    <MarkActionText
                                        onClick={() => {
                                            setOpenMarkUpOptions(false);
                                            trackChangesChecking(false);
                                            setConfirmPopup(false);
                                        }}
                                    >
                                        Skip track changes check
                                    </MarkActionText>
                                </DropDownWrapper>
                            </Dropdown.Content>
                        </Dropdown>
                    </ButtonDropWrapper>
                </ButtonsContainer>
                {!showControls && (
                    <Message>To Apply Markup please submit the document First</Message>
                )}
                {canNotAnalyzeHits && <Message>To Apply Markup please Select One Of Hits</Message>}
            </MarkupContainerWrapper>
            <ProcessingDialog isDialogHidden={!dialogVisible} closeDialog={closeDialog} />
            <PopupConfirm
                isHidden={isConfirmPopupHidden}
                onCancel={onCancel}
                onConfirm={onConfirm}
            />
        </AnalyzeDocument>
    );
};

export default MarkupPanel;
